// Structural edits: inserting, deleting and moving whole rows and columns.
//
// The cells are the easy half. The hard half is FORMULAS: deleting row 5 has to
// pull `=A9` back to `=A8`, turn `=A5` into `#REF!`, and do the same to
// `Sheet1!A9` written on a different sheet. Getting that wrong does not throw —
// it silently returns the wrong number — so every reference in the workbook goes
// through one remap, and the whole grid of cases is unit-tested.
//
// Absolute references move too. `$A$9` names a cell, and the cell has moved; the
// `$` only stops a reference sliding when a FORMULA is copied, which is a
// different operation (see translateFormula).

import type { Sheet, Workbook } from './types';
import { remapFormula } from './address';
import { parseCk } from './workbook';
import { moveMerges } from './merge';

/** Where a row/column ends up, or null if it was removed. */
export type IndexMap = (index: number) => number | null;

export type Axis = 'row' | 'col';

// ---------------------------------------------------------------------------
// The index maps
// ---------------------------------------------------------------------------

/** Deleting a sorted, de-duplicated set of indices closes the gap behind them. */
export function deletionMap(deleted: readonly number[]): IndexMap {
  const set = new Set(deleted);
  return (index) => {
    if (set.has(index)) return null;
    let shift = 0;
    for (const d of deleted) if (d < index) shift += 1;
    return index - shift;
  };
}

/** Inserting `count` at `at` pushes everything from `at` onwards down/right. */
export function insertionMap(at: number, count: number): IndexMap {
  return (index) => (index >= at ? index + count : index);
}

/**
 * Moving a contiguous run to a new slot.
 *
 * `to` is an insertion SLOT in the ORIGINAL numbering — the gap the run is
 * dropped into — which is not the index the run ends up at once it has been
 * lifted out.
 */
export function moveMap(from: number, count: number, to: number): IndexMap {
  const end = from + count - 1;
  const target = to > end ? to - count : to;
  return (index) => {
    if (index >= from && index <= end) return target + (index - from);
    let out = index;
    if (index > end) out -= count;
    if (out >= target) out += count;
    return out;
  };
}

// ---------------------------------------------------------------------------
// Applying one to a workbook
// ---------------------------------------------------------------------------

/**
 * Move the cells of one sheet, and fix up every formula in the WORKBOOK — a
 * formula on another sheet can reference this one, and leaving those behind is
 * the kind of breakage nobody notices until a total is quietly wrong.
 */
export function applyStructural(wb: Workbook, target: Sheet, axis: Axis, map: IndexMap): void {
  moveCells(target, axis, map);
  moveSizes(target, axis, map);
  moveBandState(target, axis, map);
  remapEverySheet(wb, target, axis, map);
}

/**
 * Anything else keyed by band index travels with the band.
 *
 * A filter and a dropdown belong to the column, not to the position: inserting
 * one to the left of a filtered column used to leave the filter behind, quietly
 * applying somebody else's condition to somebody else's data.
 *
 * `entityRows` is the same story a row at a time: it remembers which usage sat
 * on which row, which is how retyping a label reads as a rename rather than as
 * a deletion. Sorting the sheet must not turn the next rename into a delete.
 */
function moveBandState(sheet: Sheet, axis: Axis, map: IndexMap): void {
  moveDatabases(sheet, axis, map);
  moveMerges(sheet, axis, map);
  if (axis === 'row') {
    sheet.entityRows = remapByIndex(sheet.entityRows, map);
    sheet.rowDropdowns = remapByIndex(sheet.rowDropdowns, map);
    return;
  }
  sheet.filters = remapByIndex(sheet.filters, map);
  sheet.dropdowns = remapByIndex(sheet.dropdowns, map);
  // The sort names a column too. Left behind, deleting a column to the left of
  // the sorted one re-sorts the sheet by its neighbour — quietly, since a
  // sorted table that has been re-sorted still looks sorted.
  if (sheet.sort !== undefined) {
    const moved = map(sheet.sort.col);
    sheet.sort = moved === null ? undefined : { ...sheet.sort, col: moved };
  }
}

/**
 * A database is anchored at its title cell. It follows that cell, and goes when
 * the cell does — its formulas went with it, so there is nothing left to own.
 */
function moveDatabases(sheet: Sheet, axis: Axis, map: IndexMap): void {
  if (!sheet.databases) return;
  const next: NonNullable<Sheet['databases']> = {};
  for (const [id, db] of Object.entries(sheet.databases)) {
    const moved = map(axis === 'row' ? db.anchor.row : db.anchor.col);
    if (moved === null) continue;
    next[id] = { ...db, anchor: axis === 'row' ? { ...db.anchor, row: moved } : { ...db.anchor, col: moved } };
  }
  sheet.databases = Object.keys(next).length > 0 ? next : undefined;
}

function remapByIndex<T>(source: Record<number, T> | undefined, map: IndexMap): Record<number, T> | undefined {
  if (!source) return undefined;
  const next: Record<number, T> = {};
  for (const [key, value] of Object.entries(source)) {
    const moved = map(Number(key));
    if (moved === null) continue;
    next[moved] = value;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

function moveCells(sheet: Sheet, axis: Axis, map: IndexMap): void {
  const next: Sheet['cells'] = {};
  for (const [key, cell] of Object.entries(sheet.cells)) {
    const { row, col } = parseCk(key);
    const moved = map(axis === 'row' ? row : col);
    if (moved === null) continue; // the row/column itself went
    next[axis === 'row' ? `${moved},${col}` : `${row},${moved}`] = cell;
  }
  sheet.cells = next;
}

function moveSizes(sheet: Sheet, axis: Axis, map: IndexMap): void {
  const source = axis === 'row' ? sheet.rowHeights : sheet.colWidths;
  const next: Record<number, number> = {};
  for (const [key, size] of Object.entries(source)) {
    const moved = map(Number(key));
    if (moved === null) continue;
    next[moved] = size;
  }
  if (axis === 'row') sheet.rowHeights = next;
  else sheet.colWidths = next;
}

function remapEverySheet(wb: Workbook, target: Sheet, axis: Axis, map: IndexMap): void {
  const targetName = target.name.toLowerCase();
  for (const sheet of wb.sheets) {
    for (const [key, cell] of Object.entries(sheet.cells)) {
      if (cell.f === undefined) continue;
      const remapped = remapFormula(cell.f, (site) => {
        // A reference applies to the edited sheet when it names it, or when it
        // names none and the formula itself lives there.
        const names = site.sheet?.toLowerCase();
        const applies = names === undefined ? sheet.id === target.id : names === targetName;
        if (!applies) return { row: site.row, col: site.col };
        const moved = map(axis === 'row' ? site.row : site.col);
        if (moved === null) return null; // the referenced cell no longer exists
        return axis === 'row' ? { row: moved, col: site.col } : { row: site.row, col: moved };
      });
      if (remapped !== cell.f) sheet.cells[key] = { ...cell, f: remapped };
    }
  }
}

// ---------------------------------------------------------------------------
// The operations
// ---------------------------------------------------------------------------

/** Sorted, de-duplicated, and only the indices that exist. */
export function normalizeIndices(indices: readonly number[]): number[] {
  return [...new Set(indices)].filter((i) => i >= 1).sort((a, b) => a - b);
}

export function deleteBand(wb: Workbook, sheet: Sheet, axis: Axis, indices: readonly number[]): number {
  const list = normalizeIndices(indices);
  if (list.length === 0) return 0;
  applyStructural(wb, sheet, axis, deletionMap(list));
  if (axis === 'row') sheet.rows = Math.max(1, sheet.rows - list.length);
  else sheet.cols = Math.max(1, sheet.cols - list.length);
  return list.length;
}

export function insertBand(wb: Workbook, sheet: Sheet, axis: Axis, at: number, count: number): void {
  if (count < 1) return;
  applyStructural(wb, sheet, axis, insertionMap(at, count));
  if (axis === 'row') sheet.rows += count;
  else sheet.cols += count;
}

export function moveBand(
  wb: Workbook,
  sheet: Sheet,
  axis: Axis,
  from: number,
  count: number,
  to: number
): void {
  if (count < 1) return;
  // Dropping into either gap touching the run changes nothing.
  if (to >= from && to <= from + count) return;
  applyStructural(wb, sheet, axis, moveMap(from, count, to));
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/**
 * The permutation that sorts rows `from..to` by what column `col` holds.
 *
 * A permutation, not a rewrite, so it goes through `applyStructural` like every
 * other row move — which means the formulas follow. A sort that reorders cells
 * and leaves `=B7` pointing at row 7 is a sort that silently changes what every
 * total means, and this is the one operation where that is easy to miss because
 * the numbers all still look plausible.
 *
 * `key(row)` reads the sort value, so the caller decides whether that is the
 * raw cell or a computed one — a formula column has to sort by its RESULT.
 */
export function sortMap(
  from: number,
  to: number,
  key: (row: number) => unknown,
  descending = false
): IndexMap {
  const rows: number[] = [];
  for (let r = from; r <= to; r++) rows.push(r);

  const order = [...rows].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    /*
     * Blanks sink in BOTH directions, so they are settled here rather than by
     * compareValues — inside the comparison they would be negated along with
     * everything else, and a descending sort would open with the empty rows
     * below the data. That is not "largest first", it is the table upside down.
     */
    const ba = isBlank(ka);
    const bb = isBlank(kb);
    if (ba || bb) return ba && bb ? a - b : ba ? 1 : -1;

    const cmp = compareValues(ka, kb);
    // A stable tie: equal rows keep the order they were in, so sorting by one
    // column twice does not shuffle the rows it does not distinguish.
    return cmp !== 0 ? (descending ? -cmp : cmp) : a - b;
  });

  const moved = new Map<number, number>();
  order.forEach((was, i) => moved.set(was, rows[i]!));
  return (index) => moved.get(index) ?? index;
}

/**
 * Numbers before text; blanks are the caller's business (see sortMap).
 *
 * Numbers compare as numbers, so 900 sorts under 1000 rather than over it,
 * which is what string comparison would do.
 */
/** Built once — a collator per comparison is what made sorting thousands of rows slow. */
const NATURAL_BASE = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function compareValues(a: unknown, b: unknown): number {
  const ea = isBlank(a);
  const eb = isBlank(b);
  if (ea || eb) return ea && eb ? 0 : ea ? 1 : -1;

  const na = typeof a === 'number' ? a : Number(String(a).replace(/[^0-9.eE+-]/g, ''));
  const nb = typeof b === 'number' ? b : Number(String(b).replace(/[^0-9.eE+-]/g, ''));
  const bothNumeric = Number.isFinite(na) && Number.isFinite(nb) && looksNumeric(a) && looksNumeric(b);
  if (bothNumeric) return na === nb ? 0 : na < nb ? -1 : 1;

  return NATURAL_BASE.compare(String(a), String(b));
}

function isBlank(v: unknown): boolean {
  return v === undefined || v === null || v === '';
}

/** A number, or text that is wholly one — "$8,700" counts, "Level 3" does not. */
function looksNumeric(v: unknown): boolean {
  if (typeof v === 'number') return true;
  if (typeof v === 'boolean') return false;
  return /^\s*[-+$£€]?\s*[\d,]*\.?\d+\s*%?\s*$/.test(String(v));
}
